import { effectiveCategory, effectiveColor, isProjectSelectable, nextProjectColor, type LiveProject } from '@wiztime/core';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProjectsView } from '../../../hooks/useProjects';
import { useRunCommand } from '../../../hooks/useRunCommand';
import { useStore } from '../../../hooks/useServices';
import { errorKeyOf } from '../../../lib/errorKey';
import { useDayUi } from '../dayUiStore';
import type { NewProjectInput } from '../project/AddProjectForm';
import { QuickMarkSource } from '../quickMark';

/**
 * The "+" add popover of the quick-mark card (2026-10c §5; parent select: design 2026-10-15 §2):
 * creates the project with the next unused palette colour (`upsertProject`, appended to the
 * settings order; a child takes its parent's category and colour instead) and then
 * enters Once on it, so the user's next click or drag stamps the new project. Entering waits
 * until the project list shows the new project: the banner would otherwise read a project it
 * has not heard of yet as deleted and leave the mode at once.
 */
export function useCreateQuickProject(projects: ProjectsView): {
  readonly existingNames: readonly string[];
  /** Creates the project; resolves to null on success, or the failure reason (nothing written, or the write failed). */
  readonly create: (input: NewProjectInput) => Promise<string | null>;
  /** Renames `project` in one undoable step; resolves to the failure reason, or null. */
  readonly rename: (project: LiveProject, name: string) => Promise<string | null>;
} {
  const { t } = useTranslation();
  const store = useStore();
  const run = useRunCommand();
  const [pendingId, setPendingId] = useState<string | null>(null);
  const activated = useRef<string | null>(null);
  const known = pendingId !== null && projects.byId.has(pendingId);
  useEffect(() => {
    if (pendingId === null || !known || activated.current === pendingId) return;
    activated.current = pendingId;
    useDayUi.getState().quickMarkActivate(pendingId, QuickMarkSource.Key);
  }, [pendingId, known]);
  const all = projects.all;
  const byId = projects.byId;
  const create = useCallback(
    async ({ name, category, parentId }: NewProjectInput): Promise<string | null> => {
      // A child takes its parent's category and colour and keeps following the parent's colour.
      // A parent deleted or deactivated meanwhile is refused; it never falls back to a top-level project.
      const parent = parentId === null ? undefined : byId.get(parentId);
      if (parentId !== null && (parent === undefined || !isProjectSelectable(parent, byId))) {
        return t('newProject.parentGone');
      }
      let reason: string | null = null;
      const result = await run(async () => {
        try {
          return await store.upsertProject(
            parent === undefined
              ? { name, category, color: nextProjectColor(all) }
              : {
                  name,
                  category: effectiveCategory(parent, byId),
                  color: effectiveColor(parent, byId),
                  parentId: parent.id,
                  inheritColor: true,
                },
          );
        } catch (error) {
          reason = t(errorKeyOf(error));
          throw error;
        }
      });
      if (!result.ok) return reason ?? t(errorKeyOf(undefined));
      setPendingId(result.value.id);
      return null;
    },
    [run, store, all, byId, t],
  );
  // Renaming writes the project as it is now, not as it was when the form opened: a colour or
  // category changed meanwhile (settings, another device) must not be undone by the new name.
  const byIdRef = useRef(byId);
  useLayoutEffect(() => {
    byIdRef.current = byId;
  });
  const rename = useCallback(
    async (opened: LiveProject, name: string): Promise<string | null> => {
      const project = byIdRef.current.get(opened.id) ?? opened;
      try {
        await store.upsertProject({
          id: project.id,
          name,
          color: project.data.color,
          category: project.data.category,
          // A child that follows its parent's colour keeps following it.
          ...(typeof project.data.inheritColor === 'boolean' ? { inheritColor: project.data.inheritColor } : {}),
        });
        return null;
      } catch (error) {
        return t(errorKeyOf(error));
      }
    },
    [store, t],
  );
  return { existingNames: all.map((project) => project.data.name), create, rename };
}
